import { createAsyncThunk } from '@reduxjs/toolkit'
import { ChannelList, MenuProduct, SectionProduct, IngredientPreview } from '@/src/types/apps/gygMenu'
import { gygExternalApiClient, httpClient } from '@/src/@core/lib/httpClient'
import { MENU_PRODUCT, MENU_PRODUCT_MANAGEMENT, MENU_SECTIONS, STORES_CHANNELS, STORE_MENU } from '../../action'
import { Redux } from '../..'
import { isEmpty } from 'lodash'

export interface MenuInitialState {
  menuSections: SectionProduct[]
  menuProduct: MenuProduct[] | IngredientPreview[]
  channelList: ChannelList[]
  storeMenu: MenuData | undefined
  menuPending: boolean
  menuModelPending: boolean
  menuSuccess?: string
  menuError?: string
  menuGenerationTriggered?: boolean
  menuGenerationChannels?: MenuGenerationChannel[]
}

interface GetMenuSections {
  data: SectionProduct[]
}

export interface Menu {
  menuTemplateId: number
  name: string
  posMenuId: number
}

export interface Section {
  id: number
  title: string
  tradingHours: unknown
  subSections: SubSection[]
  displayOrder: number
}

export interface MenuData {
  menu: Menu
  store: unknown
  sections: Section[]
  commonSections: Section[]
}

export interface SubSection {
  id: number
  title: string
  displayOrder: number
  categories: Category[]
}

export interface ModifierLookup {
  id: number
  name: string
  description: string
  price: number
  nutritionalInfo: any
  imageTopDownUrl: any
  imageAngleUrl: any
  tags: string[]
  displayOrder: number
}

export interface Category {
  id: number
  posPlu: number
  name: string
  description: null | string
  displayOrder: number
  imageTopDownUrl: null | string
  imageAngleUrl: null | string
  products?: Product[]
  tagLookup: unknown
  tags?: string[]
  modifierOverrideLookup: unknown
  modifierLookup?: ModifierLookup[]
  commonSection?: number[]
  multiPart?: MultiPart[]
}

export interface MultiPart {
  id: number
  name: string
  menuFlowId: number
  posPlu: number
  description: null | string
  imageTopDownUrl: null | string
  imageAngleUrl: null | string
  displayOrder: number
  tagLookup: any[]
  multiPartSection: MultiPartSection[]
}

export interface MultiPartSection {
  name: string
  posPlu: number
  products: Product[]
  modifierLookup: unknown
  tagLookup: unknown
  displayOrder: number
  modifierOverrideLookup: unknown
}

export interface Product {
  id: number
  posPlu: number
  name: string
  description: null | string
  price: number
  nutritionalInfo: null | string
  imageTopDownUrl: null | string
  imageAngleUrl: null | string
  tags?: string[]
  defaultModifier?: number[]
  extraModifier?: number[]
  removeModifier?: number[]
  displayOrder: number
  addModifier?: number[]
  defaultFilling?: number[]
  extraFilling?: number[]
  addFilling?: number[]
}

interface GetMenuProducts {
  data: MenuProduct[] | IngredientPreview[]
}

interface GetChannelList {
  data: ChannelList[]
}

// * get menu sections
export const getmenuSectionsList = createAsyncThunk<GetMenuSections, string, Redux>(
  MENU_SECTIONS,
  async (storeId: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`/cerebro-product-companies/sections/store/${storeId}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

//* get stores channel
export const getChannelList = createAsyncThunk<GetChannelList, string, Redux>(
  STORES_CHANNELS,
  async (storeId: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`channels/store/${storeId}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

// * get store Menu
export const getStoreMenu = createAsyncThunk<MenuData, { channelId: number; storeId: string }, Redux>(
  STORE_MENU,
  async ({ channelId, storeId }, { rejectWithValue }) => {
    try {
      const response = await gygExternalApiClient(`/menu/${channelId}/${storeId}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

// Menu generation channel type
export interface MenuGenerationChannel {
  channelId: number
  channelName: string
}

// Updated product response type
export interface UpdatedProduct {
  id: number
  isEnable: boolean
  reason: string
  productName: string
}

// Enable disable product response type
export interface EnableDisableProductResponse {
  success: boolean
  jobId?: number // Job ID returned from the API for polling
  status?: string // Status from the API response
  message?: string // Message from the API response
  restaurantId?: number
  updated?: UpdatedProduct[]
  errors?: Array<{
    id?: number
    message: string
  }>
  menuGenerationTriggered?: boolean
  menuGenerationChannels?: MenuGenerationChannel[]
  summary?: {
    totalRequested: number
    successfullyUpdated: number
    failed: number
  }
}

// Poll cerebro product companies job response type
export interface PollCerebroProductCompaniesJobResponse {
  data: {
    jobId: number
    status: string
    result: any
    error: any
    createdAt: string
    updatedAt: string
  }
}

export interface PollCerebroProductCompaniesJobParams {
  jobId: number
}

//* enable disable ingredients
export const enableDisableProduct = createAsyncThunk<
  EnableDisableProductResponse,
  {
    storeId: string
    products: SectionProduct[]
    reason: string
    confirmedPermanentEnableIngredientIds?: number[]
    confirmedPermanentEnableIngredients?: Array<{
      ingredientId: number
      permanentDisabledAt: string | null
      permanentDisabledBy: number | string | null
      permanentDisabledReason: string | null
    }>

    notes?: string
  },
  Redux
>(
  MENU_PRODUCT_MANAGEMENT,
  async (
    {
      storeId,
      products,
      reason,
      confirmedPermanentEnableIngredientIds = [],
      confirmedPermanentEnableIngredients = [],
      notes
    },
    { extra, rejectWithValue }
  ) => {
    try {
      const productData = products.map(product => {
        const { id, isEnable, reason, productName } = product

        return {
          id,
          isEnable,
          reason: isEmpty(reason) ? 'N/A' : reason,
          productName,
          permanentEnableConfirmed: confirmedPermanentEnableIngredientIds.includes(id)
        }
      })

      const hasDisable = productData.some(p => !p.isEnable)
      const n = notes?.trim()

      const response = await extra.httpClient(`cerebro-product-companies/status/store/${storeId}`, {
        method: 'PUT',
        data: {
          products: productData,
          reason,
          confirmedPermanentEnableIngredientIds,
          confirmedPermanentEnableIngredients,
          ...(hasDisable && n ? { notes: n } : {})
        }
      })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

//* get ingredients
export const getProducts = createAsyncThunk<GetMenuProducts, { storeId: string; products: SectionProduct[] }, Redux>(
  MENU_PRODUCT,
  async ({ storeId, products }, { extra, rejectWithValue }) => {
    try {
      const productData = products.map(product => {
        const { id, isEnable } = product

        return { id, isEnable }
      })

      const response = await extra.httpClient(`cerebro-product-companies/product/store/${storeId}`, {
        method: 'POST',
        data: productData
      })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

//* Poll cerebro product companies job status
export const pollCerebroProductCompaniesJob = createAsyncThunk<
  PollCerebroProductCompaniesJobResponse,
  PollCerebroProductCompaniesJobParams,
  Redux
>('gygMenu/pollCerebroProductCompaniesJob', async ({ jobId }, { extra }) => {
  const response = await extra.httpClient.get(`cerebro-product-companies/jobs/${jobId}`)

  return response.data
})

//* get menu Status
export const getStoreMenuStatus = async (storeId: string): Promise<boolean> => {
  try {
    const response = await httpClient(`resto365-job/restaurant/${storeId}`)

    return response.data.data
  } catch (error: any) {
    throw error
  }
}

//* get menu Status
export const getNutritionalMenuStatus = async (storeId: string): Promise<boolean> => {
  try {
    const response = await httpClient(`resto365-job/nutritionalMenu/${storeId}`)

    return response.data.data
  } catch (error: any) {
    throw error
  }
}
